import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageWrapper } from '../components/layout/PageWrapper';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import { useAuthStore } from '../store/authStore';
import { isFreeAdmin } from '../lib/freeAdmins';
import {
  useFinanceSummary,
  useFinanceDays,
  useCreateTransfer,
  useCreateExpense,
  useUpdateExpense,
  useDeleteExpense,
} from '../hooks/useFinance';
import type { BalancePool, FinanceExpense } from '../types/finance';

const BALANCE_LABELS: Record<BalancePool, string> = {
  online: 'Онлайн (Stripe)',
  card: 'Клубная карта',
  cash: 'Наличные',
};

function formatDate(iso: string) {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function formatRu(iso: string) {
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  const [, m, d] = iso.split('-');
  return `${parseInt(d)} ${months[parseInt(m) - 1]}`;
}

function BalanceCard({ label, amount }: { label: string; amount: number }) {
  return (
    <div className="card-vintage p-5 flex flex-col gap-1">
      <p className="text-mafia-muted text-xs font-mono tracking-widest uppercase">{label}</p>
      <p className={`text-2xl font-bold ${amount < 0 ? 'text-mafia-crimson-light' : 'text-mafia-paper'}`}>
        ${amount.toFixed(2)}
      </p>
    </div>
  );
}

function TransferModal({ onClose }: { onClose: () => void }) {
  const createTransfer = useCreateTransfer();
  const [from, setFrom] = useState<BalancePool>('online');
  const [to, setTo] = useState<BalancePool>('card');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amtNum = parseFloat(amount);
    if (isNaN(amtNum) || amtNum <= 0) { setError('Введите корректную сумму'); return; }
    if (from === to) { setError('Счета должны различаться'); return; }
    try {
      await createTransfer.mutateAsync({
        date: new Date().toISOString().split('T')[0],
        fromBalance: from, toBalance: to, amountAud: amtNum,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onClose();
    } catch {
      setError('Не удалось создать трансфер');
    }
  }

  const pools: BalancePool[] = ['online', 'card', 'cash'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-mafia-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="card-vintage w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-mafia-paper font-display text-xl font-bold mb-4">Трансфер средств</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-mafia-muted text-xs font-mono">Откуда</label>
            <select value={from} onChange={(e) => setFrom(e.target.value as BalancePool)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1">
              {pools.map((p) => <option key={p} value={p}>{BALANCE_LABELS[p]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-mafia-muted text-xs font-mono">Куда</label>
            <select value={to} onChange={(e) => setTo(e.target.value as BalancePool)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1">
              {pools.map((p) => <option key={p} value={p}>{BALANCE_LABELS[p]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-mafia-muted text-xs font-mono">Сумма, AUD</label>
            <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1 focus:outline-none focus:border-mafia-gold" />
          </div>
          <div>
            <label className="text-mafia-muted text-xs font-mono">Примечание</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1 focus:outline-none focus:border-mafia-gold" />
          </div>
          {error && <p className="text-mafia-crimson-light text-xs font-mono">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={createTransfer.isPending} className="btn-gold-solid px-4 py-2 text-sm flex-1 disabled:opacity-50">
              {createTransfer.isPending ? '...' : 'Подтвердить'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-mafia-border text-mafia-paper-dim hover:text-mafia-paper transition-colors">
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddGeneralExpenseModal({ onClose }: { onClose: () => void }) {
  const createExpense = useCreateExpense();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [buyer, setBuyer] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amtNum = parseFloat(amount);
    if (!name.trim()) { setError('Введите название'); return; }
    if (isNaN(amtNum) || amtNum <= 0) { setError('Введите корректную сумму'); return; }
    if (!buyer.trim()) { setError('Укажите, кто купил'); return; }
    try {
      await createExpense.mutateAsync({
        type: 'general',
        date: new Date().toISOString().split('T')[0],
        name: name.trim(),
        amountAud: amtNum,
        buyerPlayerKey: buyer.trim(),
        buyerDisplayName: buyer.trim(),
      });
      onClose();
    } catch {
      setError('Не удалось добавить расход');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-mafia-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="card-vintage w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-mafia-paper font-display text-xl font-bold mb-4">Общий расход</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-mafia-muted text-xs font-mono">Название</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1 focus:outline-none focus:border-mafia-gold" />
          </div>
          <div>
            <label className="text-mafia-muted text-xs font-mono">Сумма, AUD</label>
            <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1 focus:outline-none focus:border-mafia-gold" />
          </div>
          <div>
            <label className="text-mafia-muted text-xs font-mono">Кто купил (ник)</label>
            <input type="text" value={buyer} onChange={(e) => setBuyer(e.target.value)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1 focus:outline-none focus:border-mafia-gold" />
          </div>
          {error && <p className="text-mafia-crimson-light text-xs font-mono">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={createExpense.isPending} className="btn-gold-solid px-4 py-2 text-sm flex-1 disabled:opacity-50">
              {createExpense.isPending ? '...' : 'Добавить'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-mafia-border text-mafia-paper-dim hover:text-mafia-paper transition-colors">
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReimburseModal({ expense, onClose }: { expense: FinanceExpense; onClose: () => void }) {
  const updateExpense = useUpdateExpense();
  const [from, setFrom] = useState<BalancePool>('card');
  const [amount, setAmount] = useState(String(expense.amountAud));
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amtNum = parseFloat(amount);
    if (isNaN(amtNum) || amtNum <= 0) { setError('Введите корректную сумму'); return; }
    try {
      await updateExpense.mutateAsync({
        entryId: expense.entryId,
        payload: {
          pk: expense.pk, sk: expense.sk,
          addReimbursement: { amount: amtNum, from, date: new Date().toISOString().split('T')[0], ...(note.trim() ? { note: note.trim() } : {}) },
        },
      });
      onClose();
    } catch {
      setError('Не удалось добавить возмещение');
    }
  }

  const totalReimbursed = expense.reimbursements.reduce((s, r) => s + r.amount, 0);
  const outstanding = expense.amountAud - totalReimbursed;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-mafia-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="card-vintage w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-mafia-paper font-display text-xl font-bold mb-1">Возместить расход</h2>
        <p className="text-mafia-paper-dim text-sm mb-4">{expense.name} — ${expense.amountAud.toFixed(2)} (к возврату: ${outstanding.toFixed(2)})</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-mafia-muted text-xs font-mono">Из счёта</label>
            <select value={from} onChange={(e) => setFrom(e.target.value as BalancePool)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1">
              {(['online', 'card', 'cash'] as BalancePool[]).map((p) => <option key={p} value={p}>{BALANCE_LABELS[p]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-mafia-muted text-xs font-mono">Сумма возмещения, AUD</label>
            <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1 focus:outline-none focus:border-mafia-gold" />
          </div>
          <div>
            <label className="text-mafia-muted text-xs font-mono">Примечание</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-2 mt-1 focus:outline-none focus:border-mafia-gold" />
          </div>
          {error && <p className="text-mafia-crimson-light text-xs font-mono">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={updateExpense.isPending} className="btn-gold-solid px-4 py-2 text-sm flex-1 disabled:opacity-50">
              {updateExpense.isPending ? '...' : 'Возместить'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-mafia-border text-mafia-paper-dim hover:text-mafia-paper transition-colors">
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GeneralExpenseRow({ expense }: { expense: FinanceExpense }) {
  const deleteExpense = useDeleteExpense();
  const [showReimburse, setShowReimburse] = useState(false);
  const totalReimbursed = expense.reimbursements.reduce((s, r) => s + r.amount, 0);
  const fullyReimbursed = totalReimbursed >= expense.amountAud - 0.005;

  return (
    <div className="flex items-center gap-3 py-3 px-4 border-b border-mafia-border last:border-b-0">
      {showReimburse && <ReimburseModal expense={expense} onClose={() => setShowReimburse(false)} />}
      <div className="flex-1">
        <p className="text-mafia-paper text-sm font-medium">{expense.name}</p>
        <p className="text-mafia-muted text-xs font-mono">
          {formatRu(expense.date ?? '')} · {expense.buyerDisplayName}
          {totalReimbursed > 0 && <span className={fullyReimbursed ? ' text-green-400' : ' text-mafia-gold'}> · возмещено ${totalReimbursed.toFixed(2)}</span>}
        </p>
      </div>
      <p className="text-mafia-paper text-sm font-bold w-20 text-right">${expense.amountAud.toFixed(2)}</p>
      {!fullyReimbursed && (
        <button type="button" onClick={() => setShowReimburse(true)}
          className="text-mafia-muted hover:text-mafia-gold text-xs font-mono underline underline-offset-2 transition-colors whitespace-nowrap">
          Возместить
        </button>
      )}
      <button type="button" onClick={() => deleteExpense.mutate({ entryId: expense.entryId, pk: expense.pk, sk: expense.sk })}
        className="text-mafia-muted hover:text-mafia-crimson-light text-xs font-mono transition-colors ml-1">
        ✕
      </button>
    </div>
  );
}

export function AdminFinancePage() {
  const { user } = useAuthStore();
  const { data: summary, isLoading: summaryLoading } = useFinanceSummary();
  const { data: daysData, isLoading: daysLoading } = useFinanceDays();
  const [showTransfer, setShowTransfer] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);

  if (!isFreeAdmin(user?.email)) {
    return <PageWrapper><p className="text-mafia-paper-dim">Нет доступа.</p></PageWrapper>;
  }

  // We don't have a separate general expenses endpoint — general expenses would need
  // to be fetched. For now this page shows balance cards, game days list, and actions.
  // General expenses are visible from individual game day pages.

  return (
    <PageWrapper>
      {showTransfer && <TransferModal onClose={() => setShowTransfer(false)} />}
      {showAddExpense && <AddGeneralExpenseModal onClose={() => setShowAddExpense(false)} />}

      <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">◆ Админ ◆</p>
      <h1 className="font-display text-4xl font-bold text-mafia-paper mb-8">Финансы</h1>

      {/* Balance cards */}
      {summaryLoading ? (
        <div className="flex gap-4 mb-8">
          {[0, 1, 2].map((i) => <div key={i} className="card-vintage p-5 flex-1 h-20 animate-pulse bg-mafia-card" />)}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <BalanceCard label="Онлайн (Stripe)" amount={summary.onlineBalance} />
          <BalanceCard label="Клубная карта" amount={summary.cardBalance} />
          <BalanceCard label="Наличные" amount={summary.cashBalance} />
        </div>
      ) : null}

      <div className="flex justify-end mb-8">
        <button type="button" onClick={() => setShowTransfer(true)}
          className="px-4 py-2 text-sm font-mono border border-mafia-border text-mafia-paper-dim hover:text-mafia-gold hover:border-mafia-gold/50 transition-colors">
          Трансфер между счетами
        </button>
      </div>

      <OrnamentDivider className="mb-8" />

      {/* Game days list */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-mafia-paper text-xl font-bold">Игровые дни</h2>
      </div>

      {daysLoading ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-2 border-mafia-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (daysData?.dates?.length ?? 0) === 0 ? (
        <p className="text-mafia-paper-dim font-mono text-sm mb-8">Нет игровых дней.</p>
      ) : (
        <div className="card-vintage mb-8">
          {daysData!.dates.map((date) => (
            <Link key={date} to={`/admin/finance/days/${date}`}
              className="flex items-center justify-between py-3 px-4 border-b border-mafia-border last:border-b-0 hover:bg-mafia-dark/50 transition-colors">
              <span className="text-mafia-paper font-medium">{formatRu(date)}</span>
              <span className="text-mafia-muted text-xs font-mono">{formatDate(date)} →</span>
            </Link>
          ))}
        </div>
      )}

      <OrnamentDivider className="mb-8" />

      {/* General expenses */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-mafia-paper text-xl font-bold">Общие расходы</h2>
        <button type="button" onClick={() => setShowAddExpense(true)}
          className="btn-gold-solid px-4 py-2 text-sm">
          + Добавить
        </button>
      </div>
      <p className="text-mafia-muted text-sm font-mono">
        Расходы, не привязанные к конкретной игре, отображаются здесь.
        Расходы игровых дней — на странице игрового дня.
      </p>
    </PageWrapper>
  );
}
